import { AtSign, Send } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import { EmojiPickerPopover } from '~/components/ui/EmojiPickerPopover';
import { MentionDropdown } from '~/components/ui/MentionDropdown';
import type { MentionEntry, MentionStaffMember } from '~/components/ui/MentionDropdown';
import { useMentionEditable } from '~/hooks/useMentionEditable';

type Variant = 'light' | 'dark';

type MentionTextareaProps = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  staff: MentionStaffMember[];
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  hint?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  initialMentions?: MentionEntry[];
  autoFocus?: boolean;
  variant?: Variant;
  showToolbar?: boolean;
  showSendButton?: boolean;
  submitButtonLabel?: string;
  /** Demo Builder: open the mention list on mount. Default off. */
  openMentionsOnMount?: boolean;
  /** Mention dropdown stacking order. Defaults to 50. */
  mentionDropdownZIndex?: number;
};

export type MentionTextareaHandle = {
  insertMention: (member: MentionStaffMember) => void;
  focus: () => void;
};

const BASE_CLASS =
  'relative w-full text-sm px-3 py-2 whitespace-pre-wrap break-words focus:outline-none overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:pointer-events-none';

const VARIANT_CLASS: Record<Variant, string> = {
  light: 'bg-white text-gray-900 caret-black disabled:bg-gray-100 empty:before:text-gray-400',
  dark: 'bg-gray-800 text-gray-100 caret-white empty:before:text-gray-500',
};

const WRAPPER_CLASS: Record<Variant, string> = {
  light:
    'rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500',
  dark: 'rounded-lg border border-gray-600 bg-gray-800 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500',
};

const TOOLBAR_CLASS: Record<Variant, string> = {
  light: 'border-t border-gray-200 bg-gray-50/80',
  dark: 'border-t border-gray-700 bg-gray-900/40',
};

const TOOLBAR_BUTTON_CLASS: Record<Variant, string> = {
  light: 'text-gray-500 hover:bg-gray-100 hover:text-gray-700',
  dark: 'text-gray-400 hover:bg-gray-700 hover:text-gray-200',
};

export const MentionTextarea = forwardRef<MentionTextareaHandle, MentionTextareaProps>(
  function MentionTextarea(
    {
      name,
      value,
      onChange,
      staff,
      placeholder,
      disabled,
      rows = 2,
      hint,
      onKeyDown,
      initialMentions,
      autoFocus,
      variant = 'light',
      showToolbar = false,
      showSendButton = false,
      submitButtonLabel = 'Send message',
      openMentionsOnMount = false,
      mentionDropdownZIndex,
    },
    ref
  ) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const editableRef = useRef<HTMLDivElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const serializedRef = useRef('');
    const isComposingRef = useRef(false);

    const syncSerialized = useCallback((wire: string) => {
      serializedRef.current = wire;
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = wire;
      }
    }, []);

    useLayoutEffect(() => {
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = serializedRef.current;
      }
    });

    const mention = useMentionEditable({
      editableRef,
      staff,
      value,
      initialMentions,
      onChange,
      onSerializedChange: syncSerialized,
      openMentionsOnMount,
    });

    useImperativeHandle(
      ref,
      () => ({
        insertMention: mention.insertMention,
        focus: () => editableRef.current?.focus(),
      }),
      [mention.insertMention]
    );

    useEffect(() => {
      // preventScroll: opening referral detail (e.g. Demo Builder) must not jump
      // the page to center the prefilled composer.
      if (autoFocus) editableRef.current?.focus({ preventScroll: true });
      // run once on mount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const editable = (
      <div
        ref={editableRef}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        aria-disabled={disabled || undefined}
        data-placeholder={placeholder}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck
        style={{ minHeight: `${rows * 1.5}rem` }}
        className={`${BASE_CLASS} ${VARIANT_CLASS[variant]} ${
          showToolbar
            ? 'rounded-t-lg border-0'
            : 'rounded-lg border focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
        } ${showToolbar ? '' : variant === 'light' ? 'border-gray-300' : 'border-gray-600'} ${
          disabled ? 'opacity-60 cursor-not-allowed' : ''
        }`}
        onInput={() => {
          if (isComposingRef.current) return;
          mention.onInput();
        }}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          mention.onInput();
        }}
        onPaste={e => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          mention.insertAtCursor(text);
        }}
        onKeyDown={e => {
          mention.onKeyDown(e);
          if (e.defaultPrevented) return;
          if (e.key === 'Enter') {
            // Let consumers handle Enter (e.g. Enter-to-send) first.
            onKeyDown?.(e);
            if (e.defaultPrevented) return;
            e.preventDefault();
            mention.insertAtCursor('\n');
            return;
          }
          onKeyDown?.(e);
        }}
      />
    );

    return (
      <>
        <div ref={wrapperRef} className={`relative ${showToolbar ? WRAPPER_CLASS[variant] : ''}`}>
          {mention.isOpen && (
            <MentionDropdown
              suggestions={mention.suggestions}
              activeIndex={mention.activeIndex}
              onSelect={mention.selectMention}
              onClose={mention.closeDropdown}
              anchorRef={wrapperRef}
              zIndex={mentionDropdownZIndex}
              stopInsideMouseDown={openMentionsOnMount}
            />
          )}
          <input type="hidden" ref={hiddenInputRef} name={name} defaultValue="" readOnly />
          {editable}
          {showToolbar && (
            <div
              className={`flex items-center justify-between gap-2 px-2 py-1 ${TOOLBAR_CLASS[variant]}`}
            >
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  disabled={disabled}
                  title="Mention someone"
                  aria-label="Mention someone"
                  onMouseDown={e => e.preventDefault()}
                  onClick={mention.triggerMention}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${TOOLBAR_BUTTON_CLASS[variant]}`}
                >
                  <AtSign className="h-4 w-4" />
                </button>
                <EmojiPickerPopover
                  disabled={disabled}
                  variant={variant}
                  onSelect={emoji => mention.insertAtCursor(emoji)}
                />
              </div>
              {showSendButton && (
                <button
                  type="submit"
                  disabled={disabled || !value.trim()}
                  title={submitButtonLabel}
                  aria-label={submitButtonLabel}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-purple-600 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-purple-600"
                >
                  <Send className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          {disabled && (
            <div
              className={`pointer-events-none absolute right-3 ${showToolbar ? 'top-3' : 'bottom-3'}`}
            >
              <svg className="h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}
        </div>
        {hint && <p className="mt-0.5 text-right text-xs text-gray-400">{hint}</p>}
      </>
    );
  }
);
